import React, { useState, useEffect, useCallback, useRef } from "react";
import { Layout, Button, notification, DatePicker, message, Space, Card, Select } from "antd";
import moment from 'moment';
import { Row, Form, Col } from "antd";
import jsPDF from 'jspdf';
import { DownloadOutlined, FilterOutlined, ClearOutlined } from "@ant-design/icons";
import * as XLSX from 'xlsx';
import autoTable from 'jspdf-autotable';

import TopHeader from "../common/TopHeader/TopHeader";
import Sidebar from "../common/Sidebar/Sidebar";
// import FooterDashboard from "../common/FooterDashboard/FooterDashboard";
import dayjs from "dayjs";
import WasteApi from "../common/Apis/WasteApi";
import Pagination from "../common/Pagination/Pagination"; // Import your custom Pagination
import "../common/Pagination/Pagination.css"; // Import the CSS

const FridgeTemperatureReport = () => {
    const { Content } = Layout;
    const [formAdd] = Form.useForm();
    const [WasteReport, setWasteReport] = React.useState([]);
    const [filteredReport, setFilteredReport] = React.useState([]);
    const [exportLoading, setExportLoading] = useState(false);
    const [filterLoading, setFilterLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    
    // Filter states
    const [storageUnitFilter, setStorageUnitFilter] = useState(null);
    const [storageUnitOptions, setStorageUnitOptions] = useState([]);
    
    // Pagination state using your custom pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const PageLoading = useCallback(() => {
        setIsLoading(true);
        setTimeout(() => {
            setIsLoading(false);
        }, 800);
    }, []);

    // Extract unique storage units from data
    const extractStorageUnitOptions = (data) => {
        const units = new Set();
        data.forEach((item) => {
            if (item?.name) {
                units.add(item.name);
            }
        });
        setStorageUnitOptions(Array.from(units).sort());
    };

    // Apply filter by storage unit
    const applyStorageUnitFilter = useCallback((data) => {
        if (!data || data.length === 0) {
            setFilteredReport([]);
            return;
        }

        let filtered = data;

        // Storage Unit filter
        if (storageUnitFilter) {
            filtered = filtered.filter(item => 
                item?.name === storageUnitFilter
            );
        }

        setFilteredReport(filtered);
        setTotalItems(filtered.length);
        setTotalPages(Math.ceil(filtered.length / pageSize));
        setCurrentPage(1);
    }, [storageUnitFilter, pageSize]);

    const handleExportToPdf = () => {
        const displayData = filteredReport.length > 0 ? filteredReport : WasteReport;
        
        if (!displayData || displayData.length === 0) {
            message.warning("No data available to export");
            return;
        }

        setExportLoading(true);

        try {
            const pdfData = [];

            displayData.forEach((date) => {
                const maxTemperatureCount = date.temperatureDTOList
                    ? date.temperatureDTOList.length
                    : 0;

                const maxStockCount = date.stockDTOList
                    ? date.stockDTOList.length
                    : 0;

                const maxInnerItemCount = Math.max(
                    maxTemperatureCount,
                    maxStockCount
                );

                Array.from({ length: maxInnerItemCount }).forEach((_, innerIndex) => {
                    if (innerIndex < maxTemperatureCount) {
                        pdfData.push([
                            innerIndex === 0 ? date?.name || '-' : '',
                            formatDate(
                                date.temperatureDTOList[innerIndex]?.modifiedDate
                            ),
                            dayjs(
                                date.temperatureDTOList[innerIndex]?.modifiedDate
                            ).format('h:mm A'),
                            `${date.temperatureDTOList[innerIndex]?.temperatureInCelsius || 0} °C`
                        ]);
                    }
                });
            });

            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4'
            });

            doc.setFontSize(16);
            doc.text('Fridge Temperature Report', 14, 15);

            const startDate = formAdd.getFieldValue('startDate');
            const endDate = formAdd.getFieldValue('endDate');

            if (startDate && endDate) {
                doc.setFontSize(10);
                doc.text(
                    `Date Range: ${dayjs(startDate).format('DD/MM/YYYY')} - ${dayjs(endDate).format('DD/MM/YYYY')}`,
                    14,
                    22
                );
            }

            // Add filter info if applied
            if (storageUnitFilter) {
                doc.setFontSize(9);
                doc.text(
                    `Storage Unit Filter: ${storageUnitFilter}`,
                    14,
                    28
                );
                autoTable(doc, {
                    startY: 35,
                    head: [[
                        'Storage Unit',
                        'Date',
                        'Time',
                        'Temperature (°C)'
                    ]],
                    body: pdfData,
                    styles: {
                        fontSize: 8,
                        cellPadding: 2
                    },
                    headStyles: {
                        fillColor: [79, 129, 189],
                        textColor: [255, 255, 255],
                        fontStyle: 'bold'
                    },
                    alternateRowStyles: {
                        fillColor: [245, 245, 245]
                    },
                    margin: {
                        left: 5,
                        right: 5
                    },
                    columnStyles: {
                        3: {
                            halign: 'right'
                        }
                    }
                });
            } else {
                autoTable(doc, {
                    startY: 30,
                    head: [[
                        'Storage Unit',
                        'Date',
                        'Time',
                        'Temperature (°C)'
                    ]],
                    body: pdfData,
                    styles: {
                        fontSize: 8,
                        cellPadding: 2
                    },
                    headStyles: {
                        fillColor: [79, 129, 189],
                        textColor: [255, 255, 255],
                        fontStyle: 'bold'
                    },
                    alternateRowStyles: {
                        fillColor: [245, 245, 245]
                    },
                    margin: {
                        left: 5,
                        right: 5
                    },
                    columnStyles: {
                        3: {
                            halign: 'right'
                        }
                    }
                });
            }

            let fileName = `Fridge_Temperature_Report_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.pdf`;

            if (startDate && endDate) {
                const startStr = dayjs(startDate).format('YYYY-MM-DD');
                const endStr = dayjs(endDate).format('YYYY-MM-DD');
                fileName = `Fridge_Temperature_Report_${startStr}_to_${endStr}.pdf`;
            }

            doc.save(fileName);

            message.success("Fridge temperature report exported successfully!");

        } catch (error) {
            console.error("Error exporting fridge temperature report:", error);
            message.error("Failed to export fridge temperature report");
        } finally {
            setExportLoading(false);
        }
    };

    const getCurrent = useCallback(() => {
        const todayDate = dayjs().format('DD/MM/YYYY');
        const tomorrowDate = dayjs().add(1, 'day').format('DD/MM/YYYY');

        formAdd.setFieldsValue({
            startDate: dayjs(todayDate, 'DD/MM/YYYY'),
            endDate: dayjs(tomorrowDate, 'DD/MM/YYYY')
        });
        WasteApi.ListStoragestemperatures(convertToUnixTimestamp(todayDate), convertToUnixTimestamp(tomorrowDate) + (+ 24 * 60 * 60 * 1000))
            .then((response) => {
                const data = response?.payloadDto || [];
                setWasteReport(data);
                extractStorageUnitOptions(data);
                setFilteredReport(data);
                setTotalItems(data.length);
                setTotalPages(Math.ceil(data.length / pageSize));
                setCurrentPage(1);
                PageLoading();
            })
            .catch((err) => {
                console.log(err);
                if (err.apiError.status) {
                    notification.open({
                        message: (
                            <span style={{ fontWeight: "bold" }}>{err.apiError.status}</span>
                        ),
                        description: err.apiError.message,
                        closeIcon: false,
                        duration: 3,
                        style: {
                            backgroundColor: "#fff2f0",
                            color: "red",
                        },
                    });
                }
            });
    }, []);

    // Apply filter when filter value changes or data changes
    useEffect(() => {
        if (WasteReport.length > 0) {
            applyStorageUnitFilter(WasteReport);
        }
    }, [WasteReport, applyStorageUnitFilter]);
    
    useEffect(() => {
        getCurrent();
    }, [getCurrent]);

    const formRef = useRef(null);

    const handlePrintButtonClick2 = () => {
        const displayData = filteredReport.length > 0 ? filteredReport : WasteReport;
        
        if (!displayData || displayData.length === 0) {
            message.warning("No data available to print");
            return;
        }

        const doc = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: [300, 210]
        });
    
        const fontSize = 12;
        const margin = 5;
        const cellWidthFactor = 0.209;
        const cellHeightFactor = 0.2;
    
        doc.setFontSize(fontSize);
    
        const table = document.querySelector('.table');
        const rows = table.querySelectorAll('tr');
    
        const pageTitle = 'Fridge Temperature Report';
        const pageTitleWidth = doc.getStringUnitWidth(pageTitle) * fontSize / doc.internal.scaleFactor;
        const pageTitleXPos = (doc.internal.pageSize.width - pageTitleWidth) / 2;
        doc.text(pageTitleXPos, margin, pageTitle);
    
        doc.setFontSize(fontSize);
        const currentDate = new Date().toLocaleString();
        const dateTimeWidth = doc.getStringUnitWidth(currentDate) * fontSize / doc.internal.scaleFactor;
        const dateTimeXPos = doc.internal.pageSize.width - margin - dateTimeWidth;
        doc.text(dateTimeXPos, margin, currentDate);
    
        let yPos = margin + fontSize;
    
        rows.forEach((row, rowIndex) => {
            const cells = row.querySelectorAll('th, td');
            let xPos = margin;
    
            cells.forEach((cell, cellIndex) => {
                const cellText = cell.textContent.trim();
                const cellWidth = cell.offsetWidth * cellWidthFactor;
                const cellHeight = row.offsetHeight * cellHeightFactor;
    
                if (yPos + cellHeight > doc.internal.pageSize.height - margin) {
                    doc.addPage();
                    yPos = margin + fontSize;
                    const pageTitle = 'Fridge Temperature Report';
                    const pageTitleWidth = doc.getStringUnitWidth(pageTitle) * fontSize / doc.internal.scaleFactor;
                    const pageTitleXPos = (doc.internal.pageSize.width - pageTitleWidth) / 2;
                    doc.text(pageTitleXPos, margin, pageTitle);
    
                    doc.setFontSize(fontSize);
                    const currentDate = new Date().toLocaleString();
                    const dateTimeWidth = doc.getStringUnitWidth(currentDate) * fontSize / doc.internal.scaleFactor;
                    const dateTimeXPos = doc.internal.pageSize.width - margin - dateTimeWidth;
                    doc.text(dateTimeXPos, margin, currentDate);
                }
    
                const isHeader = cell.tagName.toLowerCase() === 'th';
                const fontWeight = isHeader ? 'bold' : 'normal';
    
                doc.setFont(undefined, fontWeight);
    
                doc.rect(xPos, yPos, cellWidth, cellHeight);
                doc.text(xPos + 2, yPos + 5, cellText);
    
                xPos += cellWidth;
            });
    
            yPos += row.offsetHeight * cellHeightFactor;
        });
    
        window.open(doc.output('bloburl'), '_blank');
    };

    const handleSubmit = (data) => {
        setFilterLoading(true);
        const startDateN = dayjs(data.startDate).format("DD/MM/YYYY");
        const endDateN = dayjs(data.endDate).add(24, "hours").format("DD/MM/YYYY");

        WasteApi.ListStoragestemperatures(convertToUnixTimestamp(startDateN), convertToUnixTimestamp(endDateN))
            .then((response) => {
                const data = response?.payloadDto || [];
                setWasteReport(data);
                extractStorageUnitOptions(data);
                setFilteredReport(data);
                setTotalItems(data.length);
                setTotalPages(Math.ceil(data.length / pageSize));
                setCurrentPage(1);
                PageLoading();
                setFilterLoading(false);
            })
            .catch((err) => {
                console.log(err);
                setFilterLoading(false);
                if (err.apiError.status) {
                    notification.open({
                        message: (
                            <span style={{ fontWeight: "bold" }}>{err.apiError.status}</span>
                        ),
                        description: err.apiError.message,
                        closeIcon: false,
                        duration: 3,
                        style: {
                            backgroundColor: "#fff2f0",
                            color: "red",
                        },
                    });
                }
            });
    }

    // Reset storage unit filter
    const resetStorageUnitFilter = () => {
        setStorageUnitFilter(null);
        setFilteredReport(WasteReport);
        setTotalItems(WasteReport.length);
        setTotalPages(Math.ceil(WasteReport.length / pageSize));
        setCurrentPage(1);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    function formatDate(date) {
        if (!date) return '-';
        const dateNew = parseInt(date, 10);
        const options = { day: "2-digit", month: "short", year: "numeric" };
        const formattedDate = new Date(dateNew).toLocaleDateString(
            "en-US",
            options
        );

        const [month, day, year] = formattedDate.split(" ");
        const capitalizedMonth = month.toUpperCase();
        return `${day} ${capitalizedMonth} ${year}`;
    }

    function convertToUnixTimestamp(dateString) {
        const parts = dateString.split("/");
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);

        const unixTimestamp = Date.UTC(year, month, day);
        return unixTimestamp;
    }

    const handleClear = () => {
        setIsLoading(true);
        setWasteReport([]);
        setFilteredReport([]);
        setStorageUnitFilter(null);
        getCurrent();
        setTimeout(() => {
            setIsLoading(false);
        }, 800);
    };

    // Get current page data for client-side pagination
    const getCurrentPageData = () => {
        const displayData = filteredReport.length > 0 ? filteredReport : WasteReport;
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return displayData.slice(startIndex, endIndex);
    };

    const currentPageData = getCurrentPageData();

    return (
        <Layout style={{ minHeight: "100vh" }}>
            <Sidebar />
            <Layout>
                <TopHeader onPrintResponse2={handlePrintButtonClick2} />
                {isLoading && (
                    <div className="spinner-overlay" id="spinnerOverlay">
                        <div className="spinner"></div>
                    </div>
                )}

                <Content
                    className="form-content nav-content"
                    style={{
                        margin: "14px 16px 0",
                        padding: "30px",
                        background: "white",
                    }}
                >
                    <div>
                        {/* Filter Section - Separated Card */}
                        <Card 
                            style={{ 
                                marginBottom: '24px',
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                            }}
                            bodyStyle={{ padding: '20px' }}
                        >
                            <div style={{ marginBottom: '16px', borderBottom: '1px solid #f0f0f0', paddingBottom: '10px' }}>
                                <Space>
                                    <FilterOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
                                    <span style={{ fontSize: '16px', fontWeight: 500, color: '#333' }}>Filter Options</span>
                                </Space>
                            </div>
                            
                            <Form
                                form={formAdd}
                                name="fridge_temperature_filter_form"
                                layout="vertical"
                                onFinish={handleSubmit}
                            >
                                <Row gutter={[16, 0]}>
                                    <Col lg={7} md={12} sm={24} xs={24}>
                                        <Form.Item
                                            name="startDate"
                                            label="Start Date"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: "Please Select Start Date.",
                                                },
                                            ]}
                                        >
                                            <DatePicker
                                                format={"DD/MM/YYYY"}
                                                placeholder="Select start date"
                                                style={{ width: '100%', height: '40px' }}
                                                allowClear={false}
                                                suffixIcon={null}
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col lg={7} md={12} sm={24} xs={24}>
                                        <Form.Item
                                            name="endDate"
                                            label="End Date"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: "Please Select End Date.",
                                                },
                                            ]}
                                        >
                                            <DatePicker
                                                format={"DD/MM/YYYY"}
                                                placeholder="Select end date"
                                                style={{ width: '100%', height: '40px' }}
                                                allowClear={false}
                                                suffixIcon={null}
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col lg={5} md={12} sm={24} xs={24}>
                                        <Form.Item label="Storage Unit">
                                            <Select
                                                placeholder="All Storage Units"
                                                style={{ width: '100%', height: '40px' }}
                                                value={storageUnitFilter}
                                                onChange={setStorageUnitFilter}
                                                allowClear
                                                showSearch
                                                filterOption={(input, option) =>
                                                    option?.children?.toLowerCase().includes(input.toLowerCase())
                                                }
                                            >
                                                {storageUnitOptions.map(unit => (
                                                    <Select.Option key={unit} value={unit}>{unit}</Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>

                                    <Col lg={5} md={24} sm={24} xs={24}>
                                        <Form.Item label="Actions">
                                            <Space size="middle" wrap>
                                                <Button
                                                    type="primary"
                                                    htmlType="submit"
                                                    loading={filterLoading}
                                                    icon={<FilterOutlined />}
                                                    style={{ height: '40px', minWidth: '100px' }}
                                                >
                                                    Apply Filter
                                                </Button>
                                                <Button
                                                    onClick={() => handleClear()}
                                                    icon={<ClearOutlined />}
                                                    style={{ height: '40px', minWidth: '100px' }}
                                                >
                                                    Clear
                                                </Button>
                                            </Space>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Form>
                        </Card>

                        {/* Report Results Section */}
                        <Card 
                            style={{ 
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                            }}
                            bodyStyle={{ padding: '20px' }}
                        >
                            <div style={{ 
                                marginBottom: '16px', 
                                borderBottom: '1px solid #f0f0f0', 
                                paddingBottom: '10px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: '10px'
                            }}>
                                <Space>
                                    <span style={{ fontSize: '16px', fontWeight: 500, color: '#333' }}>Fridge Temperature Report Results</span>
                                    <span style={{ fontSize: '12px', color: '#999' }}>
                                        {(() => {
                                            const displayData = filteredReport.length > 0 ? filteredReport : WasteReport;
                                            return displayData.length > 0 && `(${displayData.length} records found)`;
                                        })()}
                                        {storageUnitFilter && ` - Filtered by: ${storageUnitFilter}`}
                                    </span>
                                </Space>
                                <Space>
                                    {storageUnitFilter && (
                                        <Button
                                            onClick={resetStorageUnitFilter}
                                            icon={<ClearOutlined />}
                                            style={{ height: '36px' }}
                                        >
                                            Clear Filter
                                        </Button>
                                    )}
                                    <Button
                                        type="primary"
                                        onClick={handleExportToPdf}
                                        loading={exportLoading}
                                        icon={<DownloadOutlined />}
                                        style={{ 
                                            height: '36px',
                                            backgroundColor: '#52c41a',
                                            borderColor: '#52c41a'
                                        }}
                                    >
                                        Export Temperature Report PDF
                                    </Button>
                                </Space>
                            </div>
                            
                            <div className="dateFilterTable" style={{ overflowX: 'auto' }}>
                                <table className="table users-table Reportable" style={{ width: '100%', minWidth: '600px' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ padding: '12px', textAlign: 'center' }}>Storage Unit</th>
                                            <th style={{ padding: '12px', textAlign: 'center' }}>Date</th>
                                            <th style={{ padding: '12px', textAlign: 'center' }}>Time</th>
                                            <th style={{ padding: '12px', textAlign: 'center' }} className="text-end">Temperature</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {currentPageData.length === 0 ? (
                                            <tr>
                                                <td colSpan="4" className="text-center" style={{ padding: '40px' }}>
                                                    <img
                                                        src="/assest/Images/emptyIcon.svg"
                                                        className="logo-png mt-3"
                                                        style={{ width: "10%" }}
                                                        alt="No data"
                                                    />
                                                    <br />
                                                    No temperature data available
                                                </td>
                                            </tr>
                                        ) : (
                                            currentPageData.map((date, index) => {
                                                const maxTemperatureCount = date.temperatureDTOList ? date.temperatureDTOList.length : 0;
                                                const maxStockCount = date.stockDTOList ? date.stockDTOList.length : 0;
                                                const maxInnerItemCount = Math.max(maxTemperatureCount, maxStockCount);
                                                
                                                return Array.from({ length: maxInnerItemCount }).map((_, innerIndex) => (
                                                    <tr key={`${index}-${innerIndex}`}>
                                                        <td className="CleanTable trBorder text-start text-center" style={{ padding: '10px' }}>
                                                            {innerIndex === 0 ? date?.name : ''}
                                                        </td>
                                                        {innerIndex < maxTemperatureCount ? (
                                                            <>
                                                                <td className="CleanTable trBorder text-start text-center" style={{ padding: '10px' }}>
                                                                    {formatDate(date.temperatureDTOList[innerIndex]?.modifiedDate)}
                                                                </td>
                                                                <td style={{ width: '15%', padding: '10px' }} className="CleanTable trBorder text-start text-center">
                                                                    {dayjs(date.temperatureDTOList[innerIndex]?.modifiedDate).format('h:mm A')}
                                                                </td>
                                                                <td style={{ width: '15%', padding: '10px', textAlign: 'end' }} className="CleanTable trBorder text-end text-center">
                                                                    {date.temperatureDTOList[innerIndex]?.temperatureInCelsius} °C
                                                                </td>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <td className="CleanTable trBorder text-start text-center" style={{ padding: '10px' }}>-</td>
                                                                <td style={{ width: '15%', padding: '10px' }} className="CleanTable trBorder text-start text-center">-</td>
                                                                <td style={{ width: '15%', padding: '10px' }} className="CleanTable trBorder text-end text-center">-</td>
                                                            </>
                                                        )}
                                                    </tr>
                                                ));
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            
                            {/* Custom Pagination */}
                            {(() => {
                                const displayData = filteredReport.length > 0 ? filteredReport : WasteReport;
                                return displayData.length > 0 && (
                                    <div style={{ marginTop: '20px' }}>
                                        <Pagination
                                            currentPage={currentPage}
                                            totalPages={totalPages}
                                            onPageChange={handlePageChange}
                                            maxVisiblePages={5}
                                            showPageInfo={true}
                                            totalItems={totalItems}
                                            pageSize={pageSize}
                                        />
                                    </div>
                                );
                            })()}
                        </Card>
                    </div>
                </Content>

                {/* <FooterDashboard /> */}
            </Layout>
        </Layout>
    );
};

export default FridgeTemperatureReport;